<template>
  <div
    v-click-outside="() => (isOpen = false)"
    class="dropdown"
    :class="classes"
  >
    <Button
      class="dropdown__button"
      :theme="theme"
      :form="form"
      width="full"
      size="medium"
      @click="toggleDropDown"
    >
      <template v-if="title">{{ title }} - </template>{{ selectedOptionLabel }}
      <i class="icon" :class="arrowIcon" />
    </Button>
    <Transition name="dropdown-fade">
      <ul v-if="isOpen" class="dropdown__panel">
        <li
          v-for="option in options"
          :key="option.value"
          class="dropdown__item"
        >
          <button
            type="button"
            class="dropdown__item-button"
            :disabled="option.disabled"
            @click="handleOptionClick(option)"
          >
            <span class="label">{{ option.label }}</span>
            <span class="status">{{ option.status }}</span>
          </button>
        </li>
      </ul>
    </Transition>
  </div>
</template>

<script>
import Button from "@/components/ButtonV2.vue";

export default {
  name: "DropDown",
  components: {
    Button,
  },
  props: {
    direction: {
      type: String,
      default: "down",
      validator: (value) => {
        return ["up", "down"].includes(value);
      },
    },
    theme: {
      type: String,
      default: "white",
    },
    form: {
      type: String,
      default: "outline",
    },
    title: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    selected: {
      type: String,
      default: null,
    },
    defaultLabel: {
      type: String,
      default: "Select",
    },
    transparent: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["changed"],
  data() {
    return {
      isOpen: false,
      selectedOption: null,
    };
  },
  computed: {
    arrowIcon() {
      return this.isOpen ? "icon-arrow-up" : "icon-arrow-down";
    },
    selectedOptionLabel() {
      return this.selectedOption
        ? this.selectedOption.label
        : this.defaultLabel;
    },
    classes() {
      return {
        "dropdown--up": this.direction === "up",
        "dropdown--error": this.error,
      };
    },
  },
  mounted() {
    if (this.selected) {
      this.selectedOption = this.options.find(
        (option) => option.value === this.selected
      );
    }
  },
  methods: {
    handleOptionClick(option) {
      // Add haptic feedback for mobile devices
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      this.selectedOption = option;
      this.$emit("changed", option.value);
      this.isOpen = false;
    },
    toggleDropDown() {
      this.isOpen = !this.isOpen;
    },
    doStuff() {
      this.isOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";

.dropdown {
  position: relative;

  &--error {
    :deep(.button) {
      border-color: $color-red;
      color: $color-red;
    }
  }

  &__item-button {
    background: transparent;
    border: none;
    padding: 16px 20px; // Increased padding for better touch targets
    display: block;
    width: 100%;
    z-index: 1;
    text-align: left;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 48px; // Minimum touch target size for mobile
    transition: background-color 0.2s ease;

    // Mobile-specific improvements
    @include respond-below(md) {
      padding: 20px 24px; // Even larger touch targets on mobile
      min-height: 56px;
    }

    &:disabled {
      cursor: not-allowed;
      color: rgba($color-grey-dark, 0.2);
    }

    &:hover,
    &:focus {
      background-color: $color-grey-light;
      outline: none;
    }

    &:active {
      background-color: darken($color-grey-light, 5%);
      transform: scale(0.98); // Subtle press feedback
    }

    .label {
      @include editorial-body(14px);
      color: $color-black;

      @include respond-below(md) {
        @include editorial-body(16px); // Larger text on mobile
      }
    }

    .status {
      @include utility-label(14px);
      font-weight: 400;
      text-transform: none;
      color: $color-grey-dark;
    }
  }

  &__panel {
    position: absolute;
    left: 0;
    right: 0;
    background-color: $color-white;
    z-index: 2;
    border: 1px solid $color-grey-light;
    overflow: hidden;
    margin-top: 4px;

    .dropdown--up & {
      bottom: 100%;
      margin-top: 0;
      margin-bottom: 4px;
    }

    // Mobile-specific improvements
    @include respond-below(md) {
      border: 1px solid $color-grey-light;
      margin-top: 6px;
    }
  }

  :deep() {
    .button .text {
      text-align: left;
      display: block;
    }
  }

  &__button {
    position: relative;
    text-align: left;
    padding: 9px 12px;
  }
}

.icon {
  font-size: 1em;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.2s ease;
}

// Dropdown fade animation
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s ease;
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
